import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { selectUser } from "../../store/slice/user";
import SubPageNav from "../../components/SubPageNav";
import SiteFooter from "../../components/SiteFooter";
import { getStaticPage } from "../../services/data/staticPages";
import { Style } from "./style";

const highlightIcons = ["👥", "📈", "🔗", "🏷️", "📊"];

// Mirrors new-design/o7events-frontend/contact-sales.html exactly: hero,
// a "Get in Touch" email card (mailto), and a "Why Choose Enterprise?"
// info list ending in a "Speak to Sales Directly" phone card. Content is
// backend-driven (admin "Static Pages" > Contact Us) with the original
// hardcoded copy kept as a fallback. There's still no actual form here —
// it's mailto/tel links, matching HANDOFF.md's original note that this page
// needs no backend for its (nonexistent) form logic.
const ContactSales = () => {
  const { t } = useTranslation();
  const { language } = useSelector(selectUser);
  const isArabic = language === "ar";

  const [apiData, setApiData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getStaticPage("contact-sales")
      .then((res) => {
        if (!cancelled) setApiData(res.data.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const tr = (item, field) =>
    item?.translation?.[language]?.[field] || item?.translation?.en?.[field] || null;

  const heroItem = apiData?.hero?.[0];
  const heroTitle = tr(heroItem, "title") || t("marketing.contactSales.title");
  const heroSubtitle = tr(heroItem, "subtitle") || t("marketing.contactSales.subtitle");

  const getInTouchItem = apiData?.get_in_touch?.[0];
  const getInTouchTitle = tr(getInTouchItem, "title") || t("marketing.contactSales.getInTouch");
  const getInTouchBody = tr(getInTouchItem, "body") || t("marketing.contactSales.getInTouchBody");

  const emailCardItem = apiData?.email_card?.[0];
  const emailCardTitle = tr(emailCardItem, "title") || t("marketing.contactSales.emailUs");
  const emailCardBody = tr(emailCardItem, "body") || t("marketing.contactSales.emailUsBody");
  const emailAddress = tr(emailCardItem, "button_label") || "pr@o7events.com";

  const enterpriseIntroItem = apiData?.enterprise_intro?.[0];
  const enterpriseIntroTitle = tr(enterpriseIntroItem, "title") || t("marketing.pricing.whyChooseEnterprise");

  const hardcodedHighlights = [
    { key: "dedicatedSupport", icon: "👥" },
    { key: "customPricing", icon: "📈" },
    { key: "enterpriseCustomIntegrations", icon: "🔗" },
    { key: "enterpriseWhiteLabel", icon: "🏷️" },
    { key: "enterpriseAdvancedAnalytics", icon: "📊" },
  ].map((h) => ({
    key: h.key,
    icon: h.icon,
    title: t(`marketing.pricing.${h.key}`),
    body: t(`marketing.pricing.${h.key}Body`),
  }));
  const highlights =
    apiData?.highlight?.length
      ? apiData.highlight.map((h, i) => ({
          key: h.id,
          icon: highlightIcons[i] || highlightIcons[highlightIcons.length - 1],
          title: tr(h, "title"),
          body: tr(h, "body"),
        }))
      : hardcodedHighlights;

  const phoneCardItem = apiData?.phone_card?.[0];
  const phoneCardTitle = tr(phoneCardItem, "title") || t("marketing.contactSales.speakToSalesDirectly");
  const phoneCardBody = tr(phoneCardItem, "body") || t("marketing.contactSales.callUsHours");
  const phoneNumber = tr(phoneCardItem, "button_label") || "+44 20 1234 5678";

  return (
    <Box sx={Style.page}>
      <SubPageNav />
      <Box sx={Style.hero}>
        <Typography component="h1" sx={Style.heroTitle(isArabic)}>
          {heroTitle}
        </Typography>
        <Typography sx={Style.subtitle}>
          {heroSubtitle}
        </Typography>
      </Box>

      <Box sx={Style.container}>
        <Box sx={Style.formSection}>
          <Typography component="h2" sx={Style.sectionHeading(isArabic)}>
            {getInTouchTitle}
          </Typography>
          <Typography sx={Style.bodyText}>
            {getInTouchBody}
          </Typography>
          <Box sx={Style.emailCard}>
            <Box
              component="svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
              sx={Style.emailIcon}
            >
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
            </Box>
            <Typography component="h3" sx={Style.emailHeading(isArabic)}>
              {emailCardTitle}
            </Typography>
            <Typography sx={Style.emailBody}>
              {emailCardBody}
            </Typography>
            <Typography
              component="a"
              href={`mailto:${emailAddress}`}
              sx={Style.emailLink}
            >
              {emailAddress}
            </Typography>
          </Box>
        </Box>

        <Box>
          <Typography component="h2" sx={Style.sectionHeading(isArabic)}>
            {enterpriseIntroTitle}
          </Typography>
          {highlights.map((h) => (
            <Box key={h.key} sx={Style.infoItem}>
              <Typography component="h3" sx={Style.infoTitle(isArabic)}>
                <Box component="span" sx={Style.infoIcon}>{h.icon}</Box>
                {h.title}
              </Typography>
              <Typography sx={Style.infoBody}>
                {h.body}
              </Typography>
            </Box>
          ))}

          <Box sx={Style.salesCard}>
            <Typography component="h3" sx={Style.salesHeading(isArabic)}>
              {phoneCardTitle}
            </Typography>
            <Typography sx={Style.salesBody}>
              {phoneCardBody}
            </Typography>
            <Typography component="a" href={`tel:${phoneNumber.replace(/\s+/g, "")}`} sx={Style.salesPhone}>
              {phoneNumber}
            </Typography>
          </Box>
        </Box>
      </Box>
      <SiteFooter />
    </Box>
  );
};

export default ContactSales;
